<template>
    <div id="tag-dialog">
        <slot name="button" v-if="!inlineMode">
            <div class="tag-dialog-container">
                <span
                    class="tag-dialog-element"
                    v-tippy="{
                        followCursor: true,
                        placement: 'bottom-end',
                    }"
                    :content="tag.description"
                    :key="tag._id"
                    :style="{ '--tag-color': tag.color }"
                    v-for="tag of activeTags"
                >
                    <div>
                        {{ tag.name.slice(0, 15) + (tag.name.length >= 15 ? "..." : "") }}
                    </div>
                </span>
                <a-button
                    v-if="!hideAdd"
                    icon="plus"
                    size="small"
                    type="primary"
                    class="classic-button"
                    style="margin-left: 2px; margin-top: 1px"
                    @click="openPopover = true"
                />
            </div>
        </slot>
        <div v-if="inlineMode" class="inline-tag-dialog">
            <div class="popover-content">
                <a-input :placeholder="$t('message.search')" v-model="search" />
                <div class="popover-content-labels">
                    <h6>{{ $t("message.labels") }}</h6>
                    <div class="more-actions">
                        <div v-for="tag in sortedTags" :key="tag._id" class="action-button" @click="checkedTag(tag)">
                            <a-checkbox :checked="tempChecked[tag._id]" />
                            <div class="action-button-text" :style="{ '--tagColor': tag.color }">
                                <div class="tag-circle"></div>
                                {{ tag.name }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-actions">
                    <a-button @click="cancelChanges">{{ $t("message.cancel")}}</a-button>
                    <a-button type="primary" @click="saveChanges">{{ $t("message.save")}}</a-button>
                </div>
            </div>
        </div>
        <a-popover v-if="!inlineMode" v-model="openPopover" trigger="click" overlayClassName="tag-dialog-popover" placement="topLeft">
            <div slot="content">
                <div class="popover-content">
                    <a-input :placeholder="$t('message.search')" v-model="search" />
                    <div class="popover-content-labels">
                        <h6>{{ $t("message.labels") }}</h6>
                        <div class="more-actions">
                            <div v-for="tag in filteredTags" :key="tag._id" class="action-button" @click="checkedTag(tag)">
                                <a-checkbox :checked="tempChecked[tag._id]" />
                                <div class="action-button-text" :style="{ '--tagColor': tag.color }">
                                    <div class="tag-circle"></div>
                                    {{ tag.name }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-actions">
                        <a-button @click="cancelChanges">{{ $t("message.cancel")}}</a-button>
                        <a-button type="primary" @click="saveChanges">{{ $t("message.save")}}</a-button>
                    </div>
                </div>
            </div>
        </a-popover>
    </div>
</template>

<script>
import _ from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
    name: "TagDialog",
    props: {
        value: Array,
        hideAdd: Boolean,
        inlineMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        const checked = (this.value || []).reduce((acc, x) => {
            acc[x] = true;
            return acc;
        }, {});
        return {
            checked,
            tempChecked: { ...checked },
            search: "",
            openPopover: false,
        };
    },
    computed: {
        activeTags() {
            const ids = Object.keys(this.checked).filter((id) => this.checked[id]);
            return this.tags.filter((t) => ids.includes(t._id));
        },
        sortedTags() {
            const activeTagIds = Object.keys(this.tempChecked).filter((id) => this.tempChecked[id]);
            const activeTags = this.tags.filter((t) => activeTagIds.includes(t._id));
            const inactiveTags = this.tags.filter((t) => !activeTagIds.includes(t._id));

            let result = [...activeTags, ...inactiveTags];

            const searchTerm = this.search.trim().toLowerCase();
            if (searchTerm) {
                result = result.filter(tag =>
                    tag.name.toLowerCase().includes(searchTerm)
                );
            }

            return result;
        },
        filteredTags() {
            const searchTerm = this.search.trim().toLowerCase();
            if (!searchTerm) {
                return this.tags;
            }
            return this.tags.filter(tag =>
                tag.name.toLowerCase().includes(searchTerm)
            );
        },
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
        ...mapState({
            tags: (state) => state.tag.tags.data,
        }),
    },
    methods: {
        ...mapActions({
            getTags: "tag/getTags",
        }),
        async getTaskTags() {
            try {
                this.tags = await this.getTags();
            } catch (error) {
                console.error("Error fetching tags:", error);
            }
        },
        checkedTag(tag) {
            this.$set(this.tempChecked, tag._id, !this.tempChecked[tag._id]);
        },
        saveChanges() {
            this.checked = { ...this.tempChecked };
            this.$emit(
                "input",
                Object.keys(this.checked).filter((id) => this.checked[id])
            );
            this.openPopover = false;
        },
        cancelChanges() {
            this.$emit('close');
            this.tempChecked = { ...this.checked };
            this.openPopover = false;
        },
    },
    mounted() {
        this.getTaskTags();
    },
    watch: {
        value: {
            deep: true,
            handler(val) {
                val = (val || []).reduce((acc, x) => {
                    acc[x] = true;
                    return acc;
                }, {});
                if (!_.isEqual(val, this.checked)) {
                    this.checked = val;
                    this.tempChecked = { ...val };
                }
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.classic-button {
    border: none;
    background-color: var(--primary-default-button-color) !important;
    color: var(--white) !important;
    font-family: Poppins;
    font-weight: 600 !important;
    font-size: 14px;
    cursor: pointer;
    padding: 3px 10px;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.inline-tag-dialog {
    .popover-content {
        padding: 5px;

        input {
            border: 1px solid var(--border-neutral-default);
            padding-right: 7px;
            height: 30px;

            &:hover,
            &:active,
            &:focus {
                box-shadow: none;
            }
        }

        .popover-content-labels {
            padding: 5px;
        }
    }

    .more-actions {
        max-height: 300px;
        overflow-y: auto;
        overflow-x: hidden;

        :not(:last-child).action-button {
            border-bottom: 1px solid var(--border-neutral-default);
        }

        .action-button {
            display: flex;
            flex-direction: row;
            align-items: center;
            padding: 4px 10px;
            gap: 8px;
            cursor: pointer;

            &:hover {
                background: #22272d;
                border-radius: 4px;

                .action-button-icon {
                    .material-icons {
                        color: white;
                    }
                }

                .action-button-text {
                    color: white;
                }
            }

            &-icon {
                line-height: 0;

                .material-icons {
                    font-size: 18px;
                }
            }

            &-text {
                font-size: 16px;
                display: flex;
                align-items: center;
                gap: 3px;
                flex: 1;

                .tag-circle {
                    background: var(--tagColor);
                    width: 15px;
                    height: 15px;
                    border-radius: 15px;
                    border: 1px solid var(--border-neutral-default);
                }
            }
        }
    }

    .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 15px;
    }
}

.tag-span {
    padding: 2px 6px;
    border-radius: 3px;
    margin-right: 6px;
    user-select: none;
}

#tag-container {
    .tag {
        min-width: 150px;
        display: flex;
        margin-bottom: 4px;
        align-items: center;

        .custom-control.custom-checkbox {
            transform: scale(1.7);

            .custom-control-label {
                &:before {
                    border-radius: 2px;
                }
            }
        }

        &:last-child {
            margin-bottom: 0;
        }
    }

    .tag-lbl {
        user-select: none;
        flex: 1;
        border-radius: 3px;
        margin-left: 4px;
        padding: 4px;
    }
}

.tag-dialog-container {
    display: flex;
    flex-wrap: wrap;

    .tag-dialog-element {
        border-radius: 4px;
        background: var(--primary-default-button-color);
        color: var(--white);
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 1px;
        padding: 0px 4px;

        .circle {
            width: 10px;
            height: 10px;
            margin-right: 5px;
            border-radius: 100%;
        }
    }
}
</style>

<style lang="scss">
/* Global popover styles - unscoped */
.tag-dialog-popover {
    .popover-content {
        padding: 5px;

        input {
            border: 1px solid var(--border-neutral-default);
            padding-right: 7px;
            height: 30px;

            &:hover,
            &:active,
            &:focus {
                box-shadow: none;
            }
        }

        .popover-content-labels {
            padding: 5px;
        }
    }

    .more-actions {
        max-height: 300px;
        overflow-y: auto;
        overflow-x: hidden;

        .action-button:not(:last-child) {
            border-bottom: 1px solid var(--border-neutral-default);
        }

        .action-button {
            display: flex;
            flex-direction: row;
            align-items: center;
            padding: 4px 10px;
            gap: 8px;
            cursor: pointer;

            &:hover {
                background: #22272d;
                border-radius: 4px;

                .action-button-text {
                    color: white;
                }
            }

            .action-button-text {
                font-size: 16px;
                display: flex;
                align-items: center;
                gap: 3px;
                flex: 1;

                .tag-circle {
                    background: var(--tagColor);
                    width: 15px;
                    height: 15px;
                    border-radius: 15px;
                    border: 1px solid var(--border-neutral-default);
                }
            }
        }
    }

    .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 15px;
    }
}
</style>
